import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Box,
    CircularProgress,
    InputAdornment,
    MenuItem,
    Paper,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Colors from '../../library/Colors';
import SftGlobalData from '../../library/SftGlobalData';

// ============================================================
// Mock data — kept temporarily until everything below is migrated to live
// data via the auth-backend proxy. Currently only SIZE_GROUPS is referenced.
// ============================================================

const MOCK_BRANDS = [
    { id: 'NIK', name: 'Nike',        slug: 'nike',        display_name: 'Nike',        external_supplier: null,    explore_order_priority: 10,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-18 09:32' },
    { id: 'ADI', name: 'Adidas',      slug: 'adidas',      display_name: 'adidas',      external_supplier: null,    explore_order_priority: 20,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-18 09:32' },
    { id: 'PUM', name: 'Puma',        slug: 'puma',        display_name: 'PUMA',        external_supplier: null,    explore_order_priority: 30,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-17 15:10' },
    { id: 'NBA', name: 'New Balance', slug: 'new-balance', display_name: 'New Balance', external_supplier: 'NBUSA', explore_order_priority: 40,    use_external_stock: 1, fetch_not_in_stock_products: 1, visible: 0, logo_visible: 1, updated_at: '2026-04-16 11:45' },
    { id: 'RBK', name: 'Reebok',      slug: 'reebok',      display_name: 'Reebok',      external_supplier: null,    explore_order_priority: 50,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-15 17:22' },
    { id: 'UAR', name: 'Under Armour',slug: 'under-armour',display_name: 'Under Armour',external_supplier: null,    explore_order_priority: 99999, use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 0, updated_at: '2026-04-12 08:04' },
    { id: 'ASC', name: 'Asics',       slug: 'asics',       display_name: 'ASICS',       external_supplier: null,    explore_order_priority: 60,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-11 14:18' },
    { id: 'HKA', name: 'Hoka',        slug: 'hoka',        display_name: 'HOKA',        external_supplier: null,    explore_order_priority: 70,    use_external_stock: 0, fetch_not_in_stock_products: 0, visible: 1, logo_visible: 1, updated_at: '2026-04-10 10:00' },
];

const MOCK_CATEGORIES = [
    { id: 'M000', level: 1, name: 'Men',      loc_nl: 'Heren',       loc_en: 'Men',      loc_fr: 'Hommes',    order_index: 10, explore_order_priority: 10, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'W000', level: 1, name: 'Women',    loc_nl: 'Dames',       loc_en: 'Women',    loc_fr: 'Femmes',    order_index: 20, explore_order_priority: 20, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'K000', level: 1, name: 'Kids',     loc_nl: 'Kinderen',    loc_en: 'Kids',     loc_fr: 'Enfants',   order_index: 30, explore_order_priority: 30, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MSH0', level: 2, name: 'Shoes',    loc_nl: 'Schoenen',    loc_en: 'Shoes',    loc_fr: 'Chaussures',order_index: 11, explore_order_priority: 11, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MSR0', level: 2, name: 'Shirts',   loc_nl: 'Shirts',      loc_en: 'Shirts',   loc_fr: 'Chemises',  order_index: 12, explore_order_priority: 12, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'WDR0', level: 2, name: 'Dresses',  loc_nl: 'Jurken',      loc_en: 'Dresses',  loc_fr: 'Robes',     order_index: 21, explore_order_priority: 21, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'WPT0', level: 2, name: 'Pants',    loc_nl: 'Broeken',     loc_en: 'Pants',    loc_fr: 'Pantalons', order_index: 22, explore_order_priority: 22, visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MSNK', level: 3, name: 'Sneakers', loc_nl: 'Sneakers',    loc_en: 'Sneakers', loc_fr: 'Baskets',   order_index: 111,explore_order_priority: 111,visible: 1, updated_at: '2026-03-22 09:00' },
    { id: 'MBT0', level: 3, name: 'Boots',    loc_nl: 'Laarzen',     loc_en: 'Boots',    loc_fr: 'Bottes',    order_index: 112,explore_order_priority: 112,visible: 0, updated_at: '2026-02-14 16:30' },
];

const MOCK_COLORS = [
    { id: 'BLK',  name: 'Black',  hex: '#000000', loc_nl: 'Zwart',  loc_en: 'Black',  loc_fr: 'Noir',   display_name: 'Black',  visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'WHT',  name: 'White',  hex: '#FFFFFF', loc_nl: 'Wit',    loc_en: 'White',  loc_fr: 'Blanc',  display_name: 'White',  visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'RED',  name: 'Red',    hex: '#E53935', loc_nl: 'Rood',   loc_en: 'Red',    loc_fr: 'Rouge',  display_name: 'Red',    visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'BLU',  name: 'Blue',   hex: '#1976D2', loc_nl: 'Blauw',  loc_en: 'Blue',   loc_fr: 'Bleu',   display_name: 'Blue',   visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'GRN',  name: 'Green',  hex: '#43A047', loc_nl: 'Groen',  loc_en: 'Green',  loc_fr: 'Vert',   display_name: 'Green',  visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'YLW',  name: 'Yellow', hex: '#FDD835', loc_nl: 'Geel',   loc_en: 'Yellow', loc_fr: 'Jaune',  display_name: 'Yellow', visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'GRY',  name: 'Grey',   hex: '#757575', loc_nl: 'Grijs',  loc_en: 'Grey',   loc_fr: 'Gris',   display_name: 'Grey',   visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'NVY',  name: 'Navy',   hex: '#0D47A1', loc_nl: 'Marine', loc_en: 'Navy',   loc_fr: 'Marine', display_name: 'Navy',   visible: 1, updated_at: '2026-03-15 12:00' },
    { id: 'BEI',  name: 'Beige',  hex: '#D7C0A3', loc_nl: 'Beige',  loc_en: 'Beige',  loc_fr: 'Beige',  display_name: 'Beige',  visible: 0, updated_at: '2026-02-01 09:15' },
    { id: 'PNK',  name: 'Pink',   hex: '#EC407A', loc_nl: 'Roze',   loc_en: 'Pink',   loc_fr: 'Rose',   display_name: 'Pink',   visible: 1, updated_at: '2026-03-15 12:00' },
];

const MOCK_COUNTRIES = [
    { id: 1,  sft_id: 'BE', iso: 'BE', iso_3: 'BEL', iso_num: 56,  name: 'Belgium',     loc_nl: 'België',              loc_en: 'Belgium',     loc_fr: 'Belgique',    currency_code: 'EUR', phone_code: '+32',  prefered: 1, shipping_costs: 5.95, shipping_free_from: 50,  languages: 'nl,fr', visible: 1 },
    { id: 2,  sft_id: 'NL', iso: 'NL', iso_3: 'NLD', iso_num: 528, name: 'Netherlands', loc_nl: 'Nederland',           loc_en: 'Netherlands', loc_fr: 'Pays-Bas',    currency_code: 'EUR', phone_code: '+31',  prefered: 1, shipping_costs: 7.50, shipping_free_from: 75,  languages: 'nl',    visible: 1 },
    { id: 3,  sft_id: 'FR', iso: 'FR', iso_3: 'FRA', iso_num: 250, name: 'France',      loc_nl: 'Frankrijk',           loc_en: 'France',      loc_fr: 'France',      currency_code: 'EUR', phone_code: '+33',  prefered: 1, shipping_costs: 9.95, shipping_free_from: 100, languages: 'fr',    visible: 1 },
    { id: 4,  sft_id: 'DE', iso: 'DE', iso_3: 'DEU', iso_num: 276, name: 'Germany',     loc_nl: 'Duitsland',           loc_en: 'Germany',     loc_fr: 'Allemagne',   currency_code: 'EUR', phone_code: '+49',  prefered: 0, shipping_costs: 9.95, shipping_free_from: 100, languages: 'de',    visible: 1 },
    { id: 5,  sft_id: 'LU', iso: 'LU', iso_3: 'LUX', iso_num: 442, name: 'Luxembourg',  loc_nl: 'Luxemburg',           loc_en: 'Luxembourg',  loc_fr: 'Luxembourg',  currency_code: 'EUR', phone_code: '+352', prefered: 0, shipping_costs: 7.50, shipping_free_from: 75,  languages: 'fr,de', visible: 1 },
    { id: 6,  sft_id: 'GB', iso: 'GB', iso_3: 'GBR', iso_num: 826, name: 'United Kingdom', loc_nl: 'Verenigd Koninkrijk', loc_en: 'United Kingdom', loc_fr: 'Royaume-Uni', currency_code: 'GBP', phone_code: '+44', prefered: 0, shipping_costs: 15.00, shipping_free_from: 150, languages: 'en', visible: 0 },
    { id: 7,  sft_id: 'ES', iso: 'ES', iso_3: 'ESP', iso_num: 724, name: 'Spain',       loc_nl: 'Spanje',              loc_en: 'Spain',       loc_fr: 'Espagne',     currency_code: 'EUR', phone_code: '+34',  prefered: 0, shipping_costs: 12.95,shipping_free_from: 125, languages: 'es',    visible: 1 },
    { id: 8,  sft_id: 'IT', iso: 'IT', iso_3: 'ITA', iso_num: 380, name: 'Italy',       loc_nl: 'Italië',              loc_en: 'Italy',       loc_fr: 'Italie',      currency_code: 'EUR', phone_code: '+39',  prefered: 0, shipping_costs: 12.95,shipping_free_from: 125, languages: 'it',    visible: 1 },
];

const MOCK_SEASONS = [
    { id: 'S26', name: 'SS26',     loc_nl: 'Lente / Zomer 2026',   loc_en: 'Spring / Summer 2026',    loc_fr: 'Printemps / Été 2026',       explore_order_priority: 10,    visible: 1, updated_at: '2026-01-15 09:00' },
    { id: 'W25', name: 'FW25',     loc_nl: 'Herfst / Winter 2025', loc_en: 'Fall / Winter 2025',      loc_fr: 'Automne / Hiver 2025',       explore_order_priority: 20,    visible: 1, updated_at: '2025-08-01 09:00' },
    { id: 'S25', name: 'SS25',     loc_nl: 'Lente / Zomer 2025',   loc_en: 'Spring / Summer 2025',    loc_fr: 'Printemps / Été 2025',       explore_order_priority: 30,    visible: 0, updated_at: '2025-01-15 09:00' },
    { id: 'W24', name: 'FW24',     loc_nl: 'Herfst / Winter 2024', loc_en: 'Fall / Winter 2024',      loc_fr: 'Automne / Hiver 2024',       explore_order_priority: 40,    visible: 0, updated_at: '2024-08-01 09:00' },
    { id: 'ALL', name: 'All',      loc_nl: 'Alle seizoenen',       loc_en: 'All seasons',             loc_fr: 'Toutes les saisons',         explore_order_priority: 99999, visible: 1, updated_at: '2024-01-01 09:00' },
];

const MOCK_SIZES = [
    { id: 1, size_x_description: 'XS', size_y_description: '',    group: 10, order: 10, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 2, size_x_description: 'S',  size_y_description: '',    group: 10, order: 20, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 3, size_x_description: 'M',  size_y_description: '',    group: 10, order: 30, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 4, size_x_description: 'L',  size_y_description: '',    group: 10, order: 40, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 5, size_x_description: 'XL', size_y_description: '',    group: 10, order: 50, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 6, size_x_description: 'XXL',size_y_description: '',    group: 10, order: 60, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 7, size_x_description: '38', size_y_description: '',    group: 20, order: 10, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 8, size_x_description: '40', size_y_description: '',    group: 20, order: 20, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 9, size_x_description: '42', size_y_description: '',    group: 20, order: 30, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 10, size_x_description: '44',size_y_description: '',    group: 20, order: 40, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 11, size_x_description: '32',size_y_description: '30',  group: 30, order: 10, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 12, size_x_description: '34',size_y_description: '30',  group: 30, order: 20, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 13, size_x_description: '34',size_y_description: '32',  group: 30, order: 30, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 14, size_x_description: '36',size_y_description: '32',  group: 30, order: 40, active: 1, updated_at: '2025-11-10 10:00' },
    { id: 15, size_x_description: '38',size_y_description: '34',  group: 30, order: 50, active: 1, updated_at: '2025-11-10 10:00' },
];

const SIZE_GROUPS = {
    10: 'Clothing (alpha)',
    20: 'Shoes (EU)',
    30: 'Pants (waist × length)',
    99999: 'Ungrouped',
};

// log_crons based — this is what actually gets written by background sync jobs
const MOCK_SYNC_HISTORY = [
    { id: 3303843, name: 'full_product_sync',    started_on: '2026-04-21 08:15:02', ended_on: '2026-04-21 08:17:10', status: 1, rows_affected: 1245 },
    { id: 3303822, name: 'image_sync',           started_on: '2026-04-21 06:00:01', ended_on: '2026-04-21 06:00:43', status: 1, rows_affected: 89 },
    { id: 3303801, name: 'reservation_sync',     started_on: '2026-04-20 22:00:00', ended_on: '2026-04-20 22:01:07', status: 1, rows_affected: 153 },
    { id: 3303780, name: 'brand_logo_sync',      started_on: '2026-04-20 14:30:00', ended_on: '2026-04-20 14:30:18', status: 1, rows_affected: 12 },
    { id: 3303759, name: 'full_product_sync',    started_on: '2026-04-20 08:15:00', ended_on: '2026-04-20 08:15:05', status: 0, rows_affected: 0 },
    { id: 3303738, name: 'reservation_sync',     started_on: '2026-04-19 22:00:01', ended_on: '2026-04-19 22:01:26', status: 1, rows_affected: 203 },
    { id: 3303717, name: 'attribute_sync',       started_on: '2026-04-19 08:15:02', ended_on: '2026-04-19 08:15:24', status: 1, rows_affected: 45 },
    { id: 3303696, name: 'reservation_sync',     started_on: '2026-04-18 22:00:00', ended_on: '2026-04-18 22:01:11', status: 1, rows_affected: 178 },
    { id: 3303675, name: 'full_product_sync',    started_on: '2026-04-18 08:15:00', ended_on: '2026-04-18 08:17:05', status: 1, rows_affected: 1240 },
    { id: 3303654, name: 'reservation_sync',     started_on: '2026-04-17 22:00:00', ended_on: '2026-04-17 22:00:58', status: 1, rows_affected: 134 },
    { id: 3303633, name: 'image_sync',           started_on: '2026-04-17 06:00:00', ended_on: '2026-04-17 06:00:52', status: 1, rows_affected: 203 },
    { id: 3303612, name: 'full_product_sync',    started_on: '2026-04-17 08:15:00', ended_on: '2026-04-17 08:17:20', status: 1, rows_affected: 1238 },
];

const MOCK_TRANSLATIONS = [
    { id: 1, key: 'add_to_cart',          nl: 'Toevoegen aan winkelmand',                  en: 'Add to cart',                    fr: 'Ajouter au panier' },
    { id: 2, key: 'checkout',             nl: 'Afrekenen',                                 en: 'Checkout',                       fr: 'Paiement' },
    { id: 3, key: 'total',                nl: 'Totaal',                                    en: 'Total',                          fr: 'Total' },
    { id: 4, key: 'shipping',             nl: 'Verzending',                                en: 'Shipping',                       fr: 'Livraison' },
    { id: 5, key: 'free_shipping_from',   nl: 'Gratis verzending vanaf €{amount}',         en: 'Free shipping from €{amount}',   fr: 'Livraison gratuite à partir de €{amount}' },
    { id: 6, key: 'out_of_stock',         nl: 'Uitverkocht',                               en: 'Out of stock',                   fr: 'Rupture de stock' },
    { id: 7, key: 'more_info',            nl: 'Meer info',                                 en: 'More info',                      fr: "Plus d'infos" },
    { id: 8, key: 'contact_us',           nl: 'Neem contact op',                           en: 'Contact us',                     fr: 'Contactez-nous' },
    { id: 9, key: 'size_guide',           nl: 'Maattabel',                                 en: 'Size guide',                     fr: 'Guide des tailles' },
    { id: 10, key: 'return_policy',       nl: 'Retourbeleid',                              en: 'Return policy',                  fr: 'Politique de retour' },
];

// ============================================================
// Helpers
// ============================================================

// Defensive: MySQL → PDO can return tinyint(1) as either a number, a string,
// or a boolean depending on driver flags. Treat anything that round-trips to
// the integer 1 as truthy; everything else (including "0", null, undefined)
// as falsy.
const boolYesNo = (p) => Number(p.value) === 1 ? 'Yes' : 'No';
const orNull = (p) => (p.value === null || p.value === undefined || p.value === '') ? '—' : p.value;

// Add `editable: true` (and infer the right MUI grid type) to every column
// except the ones listed in `nonEditable`. Type inference is intentionally
// narrow:
//   - renderCell === boolYesNo  → type: 'boolean'  (checkbox editor)
//   - align === 'right'         → type: 'number'   (numeric input)
//   - everything else           → string default
// This means the column-builders below stay declarative; we just flip a flag.
const makeEditable = (cols, nonEditable = ['id', 'updated_at']) =>
    cols.map(c => {
        if (nonEditable.includes(c.field)) return c;
        let type;
        if (c.renderCell === boolYesNo) type = 'boolean';
        else if (c.align === 'right')   type = 'number';
        return { ...c, editable: true, ...(type ? { type } : {}) };
    });

const durationBetween = (start, end) => {
    try {
        const s = new Date(start.replace(' ', 'T'));
        const e = new Date(end.replace(' ', 'T'));
        const diff = Math.round((e - s) / 1000);
        if (diff < 60) return `${diff}s`;
        const m = Math.floor(diff / 60);
        const sec = diff % 60;
        return `${m}m ${sec}s`;
    } catch { return '—'; }
};

// ============================================================

const WebshopDataScreen = (props) => {
    useEffect(() => {
        if (props.changeScreen) props.changeScreen('webshop');
    }, []);

    const [tabValue, setTabValue] = useState(0);
    const [syncSearch, setSyncSearch] = useState('');
    const [syncStatus, setSyncStatus] = useState('all');
    const [syncType, setSyncType] = useState('all');

    // Per-tab fetched-data cache. Key = tab index. Empty object on first
    // render — gets reset whenever the user picks a different webshop.
    const [tabData, setTabData] = useState({});
    const [tabLoading, setTabLoading] = useState({});
    const [tabError, setTabError] = useState({});

    const isDark = props.theme === 'dark';
    const cardBg = isDark ? '#2d333b' : '#ffffff';
    const borderColor = isDark ? '#3a3f47' : '#e4e7eb';
    const textColor = isDark ? Colors.dark_text : Colors.light_text;
    const mutedText = isDark ? '#b0b8c1' : '#768390';

    // ============================================================
    // Column definitions
    // ============================================================

    const BRAND_COLUMNS = [
        { field: 'id',                          headerName: 'ID',          width: 75,  display: 'flex' },
        { field: 'name',                        headerName: 'Name',        flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'slug',                        headerName: 'Slug',        flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'display_name',                headerName: 'Display',     flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'external_supplier',           headerName: 'Ext. supplier', width: 130, display: 'flex', renderCell: orNull },
        { field: 'use_external_stock',          headerName: 'Ext. stock',  width: 115, display: 'flex', renderCell: boolYesNo },
        { field: 'fetch_not_in_stock_products', headerName: 'Fetch OOS',   width: 115, display: 'flex', renderCell: boolYesNo },
        { field: 'explore_order_priority',      headerName: 'Priority',    width: 110, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'visible',                     headerName: 'Visible',     width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'logo_visible',                headerName: 'Logo',        width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',                  headerName: 'Updated',     width: 170, display: 'flex' },
    ];

    const CATEGORY_COLUMNS = [
        { field: 'id',                     headerName: 'ID',       width: 80,  display: 'flex' },
        { field: 'level',                  headerName: 'Level',    width: 80,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'name',                   headerName: 'Name',     flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_nl',                 headerName: 'NL',       flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_en',                 headerName: 'EN',       flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_fr',                 headerName: 'FR',       flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'order_index',            headerName: 'Order',    width: 95,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'explore_order_priority', headerName: 'Priority', width: 105, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'visible',                headerName: 'Visible',  width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',             headerName: 'Updated',  width: 170, display: 'flex' },
    ];

    const COLOR_COLUMNS = [
        { field: 'id',       headerName: 'ID',    width: 80,  display: 'flex' },
        { field: 'name',     headerName: 'Name',  flex: 1,    minWidth: 120, display: 'flex' },
        {
            field: 'hex',
            headerName: 'Swatch',
            width: 150,
            display: 'flex',
            renderCell: (p) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 0.75,
                        border: '1px solid rgba(0,0,0,0.15)',
                        backgroundColor: p.value,
                        flexShrink: 0,
                    }} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
                </Box>
            ),
        },
        { field: 'loc_nl',  headerName: 'NL',      flex: 1, minWidth: 110, display: 'flex' },
        { field: 'loc_en',  headerName: 'EN',      flex: 1, minWidth: 110, display: 'flex' },
        { field: 'loc_fr',  headerName: 'FR',      flex: 1, minWidth: 110, display: 'flex' },
        { field: 'visible', headerName: 'Visible', width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at', headerName: 'Updated', width: 170, display: 'flex' },
    ];

    const COUNTRY_COLUMNS = [
        { field: 'id',                 headerName: 'ID',            width: 70,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'iso',                headerName: 'ISO',           width: 75,  display: 'flex' },
        { field: 'iso_3',              headerName: 'ISO-3',         width: 85,  display: 'flex' },
        { field: 'name',               headerName: 'Name',          flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_nl',             headerName: 'NL',            flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_en',             headerName: 'EN',            flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'loc_fr',             headerName: 'FR',            flex: 1,    minWidth: 140, display: 'flex' },
        { field: 'currency_code',      headerName: 'Currency',      width: 100, display: 'flex' },
        { field: 'phone_code',         headerName: 'Phone code',    width: 110, display: 'flex' },
        { field: 'prefered',           headerName: 'Preferred',     width: 115, display: 'flex', renderCell: boolYesNo },
        { field: 'shipping_costs',     headerName: 'Shipping',      width: 120, align: 'right', headerAlign: 'right', display: 'flex', renderCell: (p) => `€${Number(p.value).toFixed(2)}` },
        { field: 'shipping_free_from', headerName: 'Free from',     width: 120, align: 'right', headerAlign: 'right', display: 'flex', renderCell: (p) => `€${Number(p.value).toFixed(2)}` },
        { field: 'languages',          headerName: 'Languages',     width: 130, display: 'flex' },
        { field: 'visible',            headerName: 'Visible',       width: 100, display: 'flex', renderCell: boolYesNo },
    ];

    const SEASON_COLUMNS = [
        { field: 'id',                     headerName: 'ID',       width: 80,  display: 'flex' },
        { field: 'name',                   headerName: 'Code',     width: 120, display: 'flex' },
        { field: 'loc_nl',                 headerName: 'NL',       flex: 1,    minWidth: 180, display: 'flex' },
        { field: 'loc_en',                 headerName: 'EN',       flex: 1,    minWidth: 180, display: 'flex' },
        { field: 'loc_fr',                 headerName: 'FR',       flex: 1,    minWidth: 180, display: 'flex' },
        { field: 'explore_order_priority', headerName: 'Priority', width: 105, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'visible',                headerName: 'Visible',  width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',             headerName: 'Updated',  width: 170, display: 'flex' },
    ];

    const SIZE_COLUMNS = [
        { field: 'id',                 headerName: 'ID',     width: 90,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'size_x_description', headerName: 'Size X', width: 120, display: 'flex' },
        { field: 'size_y_description', headerName: 'Size Y', width: 120, display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'group',              headerName: 'Group',  flex: 1,    minWidth: 200, display: 'flex', renderCell: (p) => SIZE_GROUPS[p.value] || `Group ${p.value}` },
        { field: 'order',              headerName: 'Order',  width: 90,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'active',             headerName: 'Active', width: 100, display: 'flex', renderCell: boolYesNo },
        { field: 'updated_at',         headerName: 'Updated',width: 170, display: 'flex' },
    ];

    // Reservations / receipts. Read-only by design — these are financial
    // records and shouldn't be patched via a grid edit.
    const RESERVATION_COLUMNS = [
        { field: 'id',                 headerName: 'ID',          width: 75,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'reservation_id',     headerName: 'Order ID',    width: 130, display: 'flex' },
        {
            field: 'reservation_type',
            headerName: 'Type',
            width: 130,
            display: 'flex',
            renderCell: (p) => {
                const isReceipt = p.value === 'RECEIPT';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            backgroundColor: isReceipt ? Colors.sft_blue : '#9c27b0',
                        }} />
                        <Typography variant="body2">{p.value || '—'}</Typography>
                    </Box>
                );
            },
        },
        { field: 'delivery_type',      headerName: 'Delivery',    width: 110, display: 'flex', renderCell: (p) => p.value || '—' },
        {
            field: 'total_amount',
            headerName: 'Total',
            width: 110,
            align: 'right',
            headerAlign: 'right',
            display: 'flex',
            renderCell: (p) => p.value != null ? `€${Number(p.value).toFixed(2)}` : '—',
        },
        {
            field: 'customer_name',
            headerName: 'Customer',
            flex: 1, minWidth: 180,
            display: 'flex',
            // Synthesized client-side from the firstname + lastname columns the
            // mini-api extracts out of customer_data, so the grid stays sortable
            // and searchable by full name.
            valueGetter: (_v, row) => {
                const parts = [row.customer_firstname, row.customer_lastname].filter(Boolean);
                return parts.join(' ').trim() || '—';
            },
        },
        { field: 'customer_email',     headerName: 'Email',       flex: 1, minWidth: 200, display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'store_id',           headerName: 'Store',       width: 80,  align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'language',           headerName: 'Lang',        width: 75,  display: 'flex' },
        { field: 'payed_status',       headerName: 'Payed',       width: 120, display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'payed_via',          headerName: 'Via',         width: 90,  display: 'flex', renderCell: (p) => p.value || '—' },
        { field: 'sent_email',         headerName: 'Email sent',  width: 110, display: 'flex', renderCell: boolYesNo },
        { field: 'sent_reservation',   headerName: 'Sent',        width: 90,  display: 'flex', renderCell: boolYesNo },
        { field: 'is_queued',          headerName: 'Queued',      width: 90,  display: 'flex', renderCell: boolYesNo },
        { field: 'created_at',         headerName: 'Created',     width: 170, display: 'flex' },
    ];

    const TRANSLATION_COLUMNS = [
        { field: 'id',  headerName: 'ID',  width: 70,   display: 'flex' },
        { field: 'key', headerName: 'Key', flex: 1,     minWidth: 200, display: 'flex', renderCell: (p) => (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
        ) },
        { field: 'nl',  headerName: 'NL',  flex: 1.5,   minWidth: 200, display: 'flex' },
        { field: 'en',  headerName: 'EN',  flex: 1.5,   minWidth: 200, display: 'flex' },
        { field: 'fr',  headerName: 'FR',  flex: 1.5,   minWidth: 200, display: 'flex' },
    ];

    const SYNC_COLUMNS = [
        { field: 'id',         headerName: 'ID',       width: 100, align: 'right', headerAlign: 'right', display: 'flex' },
        { field: 'name',       headerName: 'Job',      flex: 1,    minWidth: 200, display: 'flex', renderCell: (p) => (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{p.value}</Typography>
        ) },
        { field: 'started_on', headerName: 'Started',  width: 180, display: 'flex' },
        { field: 'ended_on',   headerName: 'Ended',    width: 180, display: 'flex' },
        {
            field: 'duration',
            headerName: 'Duration',
            width: 110,
            align: 'right',
            headerAlign: 'right',
            display: 'flex',
            sortable: false,
            renderCell: (p) => durationBetween(p.row.started_on, p.row.ended_on),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            display: 'flex',
            renderCell: (p) => {
                const success = p.value === 1;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: success ? '#4caf50' : Colors.def_red }} />
                        <Typography variant="body2">{success ? 'Success' : 'Failed'}</Typography>
                    </Box>
                );
            },
        },
        { field: 'rows_affected', headerName: 'Rows', width: 110, align: 'right', headerAlign: 'right', display: 'flex' },
    ];

    // ============================================================
    // Sync filter logic — applied client-side to whatever rows the mini-api
    // returns for the Sync history tab.
    // ============================================================

    const filteredSyncHistory = useCallback((rows) => {
        const q = syncSearch.trim().toLowerCase();
        return (rows || []).filter(row => {
            if (syncStatus === 'success' && row.status != 1) return false;
            if (syncStatus === 'failed'  && row.status == 1) return false;
            if (syncType !== 'all' && row.name !== syncType) return false;
            if (q) {
                const haystack = `${row.name ?? ''} ${row.started_on ?? ''} ${row.ended_on ?? ''} ${row.id ?? ''}`.toLowerCase();
                if (!haystack.includes(q)) return false;
            }
            return true;
        });
    }, [syncSearch, syncStatus, syncType]);

    // ============================================================
    // Tab → mini-api endpoint mapping. Each entry knows which path on the
    // webshop's mini-api to GET, and (optionally) how to massage the
    // response into the row shape the DataGrid expects. `editable` controls
    // inline cell editing — sync history is a log so it stays read-only;
    // everything else PATCHes via `${path}/{id}` (overridable via patchPath).
    // ============================================================
    const TAB_ENDPOINTS = useMemo(() => ([
        { label: 'Brands',     columns: makeEditable(BRAND_COLUMNS),                                   path: '/brands',     editable: true },
        { label: 'Countries',  columns: makeEditable(COUNTRY_COLUMNS),                                 path: '/countries',  editable: true },
        // `level` is structural for categories — flagging it editable would let admins
        // break the tree silently since the mini-api PATCH whitelist excludes it.
        { label: 'Categories', columns: makeEditable(CATEGORY_COLUMNS, ['id', 'updated_at', 'level']), path: '/categories', editable: true },
        { label: 'Colors',     columns: makeEditable(COLOR_COLUMNS),                                   path: '/colors',     editable: true },
        { label: 'Seasons',    columns: makeEditable(SEASON_COLUMNS),                                  path: '/seasons',    editable: true },
        { label: 'Sizes',      columns: makeEditable(SIZE_COLUMNS),                                    path: '/sizes',      editable: true },
        { label: 'Reservations', columns: RESERVATION_COLUMNS, path: '/reservations', editable: false },
        { label: 'Sync history', columns: SYNC_COLUMNS,        path: '/admin/log',
          transform: (data) => (data?.crons || []).map((r, i) => ({ id: r.id ?? i, ...r })),
          isSync: true,
          editable: false },
        { label: 'Translations', columns: makeEditable(TRANSLATION_COLUMNS),
          path: '/localization',
          editable: true,
          // i18n stores the lookup token as `item`; the grid uses `key`. The GET
          // transform maps item→key for display; bodyTransform reverses it on PATCH.
          transform: (rows) => (rows || []).map(r => ({ ...r, key: r.item ?? r.key })),
          bodyTransform: (changes) => {
              const out = { ...changes };
              if ('key' in out) { out.item = out.key; delete out.key; }
              return out;
          },
        },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ]), [filteredSyncHistory]);

    // Find the Sync-history tab dynamically so this lookup survives tab
    // reorders or insertions.
    const syncTabIndex = useMemo(
        () => TAB_ENDPOINTS.findIndex(t => t.isSync),
        [TAB_ENDPOINTS]
    );
    const syncTypes = useMemo(() => {
        const rows = (syncTabIndex >= 0 ? tabData[syncTabIndex] : null) || [];
        const types = new Set(rows.map(r => r.name).filter(Boolean));
        return ['all', ...Array.from(types)];
    }, [tabData, syncTabIndex]);

    // Reset cache whenever the user switches webshops.
    useEffect(() => {
        setTabData({});
        setTabError({});
    }, [props.selectedWebshop?.id]);

    // Fetch the data for a tab, lazily, and cache it.
    const fetchTab = useCallback(async (idx) => {
        const def = TAB_ENDPOINTS[idx];
        if (!def) return;
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return;
        setTabLoading(prev => ({ ...prev, [idx]: true }));
        setTabError(prev => ({ ...prev, [idx]: null }));
        try {
            const result = await SftGlobalData.post('webshop&action=proxy', {
                key:        props.api,
                webshop_id: webshopId,
                sftUserId:  props.login?.sft_user_id,
                user:       props.login?.login,
                method:     'GET',
                path:       def.path,
            });
            if (result && result.success) {
                const transformed = def.transform ? def.transform(result.data) : (result.data || []);
                setTabData(prev => ({ ...prev, [idx]: Array.isArray(transformed) ? transformed : [] }));
            } else {
                setTabError(prev => ({ ...prev, [idx]: result?.message || 'Failed to load' }));
                setTabData(prev => ({ ...prev, [idx]: [] }));
            }
        } catch (e) {
            setTabError(prev => ({ ...prev, [idx]: e?.message || 'Network error' }));
            setTabData(prev => ({ ...prev, [idx]: [] }));
        } finally {
            setTabLoading(prev => ({ ...prev, [idx]: false }));
        }
    }, [TAB_ENDPOINTS, props.api, props.login?.sft_user_id, props.login?.login, props.selectedWebshop?.id]);

    // When the tab or webshop changes, fetch lazily if not already cached.
    useEffect(() => {
        if (tabData[tabValue] === undefined && !tabLoading[tabValue]) {
            fetchTab(tabValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabValue, props.selectedWebshop?.id, fetchTab]);

    // Inline cell-edit commit handler. DataGridPro fires this after the user
    // commits an edit (Enter / blur). We diff old vs new, PATCH the changed
    // fields via the proxy, and merge the refreshed row back into the cache.
    // Throwing causes DataGrid to revert the cell + invoke onProcessRowUpdateError.
    const processRowUpdate = useCallback(async (newRow, oldRow) => {
        const def = TAB_ENDPOINTS[tabValue];
        if (!def || def.editable === false) return oldRow;
        const webshopId = props.selectedWebshop?.id;
        if (!webshopId || !props.api) return oldRow;

        // Diff the two rows. id is excluded as a safety net (column is
        // non-editable anyway, but make the contract explicit).
        const changes = {};
        for (const key of Object.keys(newRow)) {
            if (key === 'id') continue;
            if (newRow[key] !== oldRow[key]) changes[key] = newRow[key];
        }
        if (Object.keys(changes).length === 0) return oldRow;

        const body = def.bodyTransform ? def.bodyTransform(changes, newRow) : changes;
        const patchPath = (def.patchPath ? def.patchPath(newRow.id) : `${def.path}/${newRow.id}`);

        const result = await SftGlobalData.post('webshop&action=proxy', {
            key:        props.api,
            webshop_id: webshopId,
            sftUserId:  props.login?.sft_user_id,
            user:       props.login?.login,
            method:     'PATCH',
            path:       patchPath,
            body,
        });
        if (!result || !result.success) {
            throw new Error(result?.message || 'Failed to save');
        }

        // Mini-api PATCH handlers return the refreshed DB row. Re-apply the
        // tab's GET transform so display-side fields (e.g. translations'
        // `key` from `item`) stay consistent with the cached list.
        const refreshed = result.data || {};
        let merged = { ...newRow, ...refreshed };
        if (def.transform) {
            const transformed = def.transform([refreshed]);
            if (transformed && transformed[0]) merged = { ...merged, ...transformed[0] };
        }

        // Update the cache so the change persists across tab switches.
        setTabData(prev => {
            const list = prev[tabValue] || [];
            return {
                ...prev,
                [tabValue]: list.map(r => r.id === merged.id ? merged : r),
            };
        });

        if (props.notify) props.notify('Saved', 2500, 'top-right', props.theme, 2);
        return merged;
    }, [TAB_ENDPOINTS, tabValue, props.api,
        props.login?.sft_user_id, props.login?.login,
        props.selectedWebshop?.id, props.notify, props.theme]);

    const handleProcessRowUpdateError = useCallback((error) => {
        if (props.notify) props.notify('Save failed: ' + (error?.message || ''), 5000, 'top-right', props.theme, 1);
    }, [props.notify, props.theme]);

    const tabs = TAB_ENDPOINTS.map((def, idx) => {
        const fetched = tabData[idx];
        const rows = def.isSync
            ? filteredSyncHistory(fetched || [])
            : (fetched || []);
        return { ...def, rows };
    });

    const currentTab = tabs[tabValue];

    return (
        <div className={(props.screenWidth < 600 ? 'not_expanded' : 'expanded') + ' container_content _80perc ' + props.theme + ' container_content_' + props.theme}>
            <Box sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ color: textColor, fontWeight: 500, mb: 0.5 }}>
                    Webshop data
                </Typography>
                <Typography variant="body2" sx={{ color: mutedText }}>
                    {props.selectedWebshop
                        ? `Live data from ${props.selectedWebshop.display_name || props.selectedWebshop.slug}.`
                        : 'Select a webshop in the sidebar to view its data.'}
                </Typography>
            </Box>

            <Paper
                elevation={0}
                sx={{
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                    backgroundColor: cardBg,
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center' }}>
                    <Tabs
                        value={tabValue}
                        onChange={(_e, v) => setTabValue(v)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ px: 1, flex: 1 }}
                    >
                        {tabs.map((t) => (
                            <Tab key={t.label} label={t.label} sx={{ textTransform: 'none', fontWeight: 500 }} />
                        ))}
                    </Tabs>
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setTabData(prev => { const n = { ...prev }; delete n[tabValue]; return n; });
                                    setTabError(prev => ({ ...prev, [tabValue]: null }));
                                    fetchTab(tabValue);
                                }}
                                disabled={!props.selectedWebshop || !!tabLoading[tabValue]}
                                sx={{ mr: 1 }}
                            >
                                <RefreshIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Box sx={{ p: 2 }}>
                    {currentTab.isSync && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                            <TextField
                                size="small"
                                placeholder="Search job name, date, or ID…"
                                value={syncSearch}
                                onChange={(e) => setSyncSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: mutedText }} fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ flex: 1, minWidth: 240 }}
                            />
                            <TextField
                                select
                                size="small"
                                label="Status"
                                value={syncStatus}
                                onChange={(e) => setSyncStatus(e.target.value)}
                                sx={{ minWidth: 160 }}
                            >
                                <MenuItem value="all">All statuses</MenuItem>
                                <MenuItem value="success">Success</MenuItem>
                                <MenuItem value="failed">Failed</MenuItem>
                            </TextField>
                            <TextField
                                select
                                size="small"
                                label="Job"
                                value={syncType}
                                onChange={(e) => setSyncType(e.target.value)}
                                sx={{ minWidth: 220 }}
                            >
                                {syncTypes.map(t => (
                                    <MenuItem key={t} value={t}>
                                        {t === 'all' ? 'All jobs' : t}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    )}

                    <Box sx={{ width: '100%', position: 'relative', minHeight: 200 }}>
                        {!props.selectedWebshop && (
                            <Box sx={{ p: 4, textAlign: 'center', color: mutedText }}>
                                <Typography variant="body2">No webshop selected — pick one in the sidebar.</Typography>
                            </Box>
                        )}
                        {props.selectedWebshop && tabError[tabValue] && (
                            <Box sx={{ p: 4, textAlign: 'center', color: Colors.def_red }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    Failed to load: {tabError[tabValue]}
                                </Typography>
                                <Typography variant="caption" sx={{ color: mutedText }}>
                                    Click the refresh icon above to try again.
                                </Typography>
                            </Box>
                        )}
                        {props.selectedWebshop && !tabError[tabValue] && (
                            <>
                                {tabLoading[tabValue] && (
                                    <Box sx={{
                                        position: 'absolute', inset: 0,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        backgroundColor: isDark ? 'rgba(45,51,59,0.6)' : 'rgba(255,255,255,0.6)',
                                        zIndex: 2,
                                    }}>
                                        <CircularProgress size={28} />
                                    </Box>
                                )}
                                <DataGridPro
                                    className={props.theme}
                                    rows={currentTab.rows}
                                    columns={currentTab.columns}
                                    autoHeight
                                    density="compact"
                                    rowHeight={40}
                                    pagination
                                    pageSizeOptions={[10, 25, 50, 100]}
                                    initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
                                    disableRowSelectionOnClick
                                    processRowUpdate={processRowUpdate}
                                    onProcessRowUpdateError={handleProcessRowUpdateError}
                                />
                            </>
                        )}
                    </Box>
                </Box>
            </Paper>
        </div>
    );
};

export default WebshopDataScreen;
